'use client';

import React, { useMemo, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Crown } from 'lucide-react';
import { House, User, Question } from '@phosphor-icons/react';
import { useActiveProject } from '@/features/project';
import { capitalize } from '@/shared/lib/text';
import { cn } from '@/shared/lib/cn';
import { NAV_REVEAL_TRANSFORM } from '@/shared/lib/bottom-bar-reveal';
import { useBreakpoint } from '@/shared/hooks/use-media-query';
import { useSidebarStore } from '@/shared/stores/sidebar-store';
import { useJobQueueStore } from '@/features/design/job-queue';
import { HistoryActivityIcon } from '@/shared/ui/HistoryActivityIcon';
import {
  useAccountIssue,
  type AccountIssueSeverity,
  type TokenBalanceFlag,
} from '@/shared/hooks/use-account-issue';
import { useRawTokenBalance } from '@/shared/api/hooks/use-token-balance';
import { formatTokenBalance } from '@/shared/lib/utils/tokenBalanceFormatter';
import { TokenBalanceBadge } from '@/shared/ui/TokenBalanceBadge';
import {
  useAccountNavGlyph,
  type AccountNavGlyph,
} from '@/shared/hooks/use-account-chrome';

const MAX_PROJECT_NAME_LENGTH = 12;

// Shared two-row tab styling. The bar imitates the native iOS tab bar (which
// Apple now mirrors): every tab is an equal-width column with the icon on the
// top row and an always-visible label below. Selection is conveyed purely by
// tint (teal/primary) — there is no selection pill.
// Each tab hugs its content at a compact fixed min-width (a comfortable tap
// target) instead of stretching with `flex-1` — otherwise the bar fans out to
// its full `max-w` on wide screens and the tabs drift far apart. The bar itself
// shrink-wraps these columns (no `w-full`) and stays centered.
const TAB_COLUMN_CLASS =
  'flex min-w-[60px] flex-col items-center justify-end gap-1 py-1 px-0.5 transition-colors duration-200 touch-manipulation';
const TAB_LABEL_CLASS =
  'max-w-full truncate text-[11px] font-medium leading-none';
// Tab glyphs sit at 22px — a notch down from the old 26px so they read at
// parity with the native bars (Apple 20pt, Android 24dp) instead of towering
// over their 11px labels. The slot reserves the same height so every tab's
// label lands on a shared baseline regardless of what fills the glyph row.
const TAB_ICON_SLOT_CLASS =
  'relative flex h-[22px] items-center justify-center';
const TAB_ICON_CLASS = 'h-[22px] w-[22px] shrink-0';
// Viewport distance from the bottom edge to the top of this floating bar,
// published on <html> so bottom-pinned page controls (the brief's Create design
// CTA) sit right above it without hardcoding the bar's geometry. Unset while the
// bar is not rendered (wide screens with the sidebar open).
const NAV_CLEARANCE_VAR = '--mobile-nav-clearance';

const tabTint = (isActive: boolean) =>
  isActive ? 'text-primary' : 'text-muted-foreground active:text-foreground';

export function MobileNavigation() {
  const t = useTranslations('navigation');
  const tBuildings = useTranslations('dashboard');
  const pathname = usePathname();
  const { isLg } = useBreakpoint();

  // Publishes NAV_CLEARANCE_VAR while the bar is mounted (callback ref: the
  // bar unmounts on wide screens through an early return below).
  const clearanceCleanupRef = useRef<(() => void) | null>(null);
  const navRef = useCallback((el: HTMLElement | null) => {
    clearanceCleanupRef.current?.();
    clearanceCleanupRef.current = null;
    if (!el) return;
    const root = document.documentElement;
    const update = () => {
      const clearance = window.innerHeight - el.getBoundingClientRect().top;
      root.style.setProperty(NAV_CLEARANCE_VAR, `${Math.max(0, clearance)}px`);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener('resize', update);
    clearanceCleanupRef.current = () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      root.style.removeProperty(NAV_CLEARANCE_VAR);
    };
  }, []);

  // Active building — resolved IDENTICALLY to the desktop sidebar (URL building
  // → last-opened → first), persisted by <ActiveProjectSync/>. This bar used to
  // derive the building from the URL only and fall back to `projects[0]`, so on
  // secondary pages (Recent/Account/Help, where the URL has no building) it
  // showed the newest building instead of the one you were working in — the two
  // navs disagreed. Sharing the resolver keeps them in lockstep.
  const { activeProjectId, activeProject } = useActiveProject();

  // Sidebar collapse state. On wide screens (lg+) this bar replaces the
  // sidebar when it's collapsed — mirroring the Apple app, where collapsing
  // drops navigation into a floating bottom bar rather than an icon rail. The
  // reveal-sidebar affordance itself lives in the top-left corner (DesktopShell),
  // not here — again matching Apple, which moved it to a leading toolbar item.
  const collapsed = useSidebarStore((s) => s.collapsed);

  // Account dot — single dot, deletion (red) wins over warning (orange).
  const accountIssue = useAccountIssue();

  // Raw balance for the token pill — `undefined` until the first seeder fires,
  // so we can hide the pill instead of flashing "0". `formatTokenBalance`
  // clamps negatives to "0" (we never show a debt balance, 2026-05-27).
  const rawTokenBalance = useRawTokenBalance();

  // Account-tab glyph — PER-BUILDING (multi-product model): a kit building reads
  // the crown, a non-kit building the token balance, off any building the global
  // account status. Resolved from the active project via the shared chrome hook
  // (parity with the desktop sidebar).
  const accountGlyph = useAccountNavGlyph(activeProject);

  // On wide screens the sidebar owns navigation, so hide this bar — unless the
  // sidebar is collapsed, in which case this bar takes over (Apple parity).
  // Below lg there is no sidebar, so the bar is always shown.
  if (isLg && !collapsed) {
    return null;
  }

  // Determine active tab. The building page is the home now, so `/` and
  // `/project/*` both light the Project tab.
  const activeTab = (() => {
    if (pathname === '/' || pathname.startsWith('/project/')) return 'project';
    if (pathname === '/history' || pathname.startsWith('/history/'))
      return 'history';
    // The Tokens screen lives under Account now (no standalone tab), so
    // /account and /account/tokens both light the Account tab.
    if (pathname === '/account' || pathname.startsWith('/account/'))
      return 'account';
    if (pathname === '/help' || pathname.startsWith('/help/')) return 'help';
    return 'project';
  })();

  // Brief attention dot on the project tab: fires for the two essential empty
  // tasks — no view (photo) uploaded yet, or an empty brief.
  const hasBriefAttention = activeProject
    ? !activeProject.attributes.goals?.trim() ||
      (activeProject.attributes.angles_count ?? 0) === 0
    : false;

  // Project tab name
  const projectTabName = activeProject
    ? truncate(capitalize(activeProject.attributes.name), MAX_PROJECT_NAME_LENGTH)
    : tBuildings('projects');

  const projectActive = activeTab === 'project';

  return (
    <nav
      ref={navRef}
      className="fixed bottom-3 left-4 right-4 z-40 flex justify-center pointer-events-none"
      // Scroll-linked reveal on the narrow concept page: slides off the bottom
      // edge at progress 0 and back at 1, tracking the scroll (no transition —
      // the motion follows the finger). Off the concept page the CSS var is
      // unset and the `, 1` fallback resolves this to 0 (no movement).
      style={{ transform: NAV_REVEAL_TRANSFORM }}
    >
      {/* Liquid Glass surface (parity with the DialogContent chrome): a more
          translucent fill + heavier blur with a saturation boost so the page
          shows through as vivid frosted glass, hairline border, diffuse shadow. */}
      <div className="pointer-events-auto flex max-w-full items-stretch gap-1 sm:gap-3 md:gap-5 rounded-[28px] bg-background/40 backdrop-blur-2xl backdrop-saturate-150 border border-border/40 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.18)] px-4 sm:px-5 py-1.5">
        {/* Project — the home tab. The building name rides as the tab label (a
            house glyph alone can't tell the user which building they're scoped
            to). */}
        {activeProjectId ? (
          <Link
            href={`/project/${activeProjectId}`}
            aria-label={projectTabName}
            className={cn(TAB_COLUMN_CLASS, tabTint(projectActive))}
          >
            <span className={TAB_ICON_SLOT_CLASS}>
              <House
                className={TAB_ICON_CLASS}
                weight={projectActive ? 'fill' : 'regular'}
              />
              {hasBriefAttention && (
                <span className="absolute -top-0.5 -end-1 w-2 h-2 rounded-full bg-warning animate-attention-pulse" />
              )}
            </span>
            <span className={TAB_LABEL_CLASS}>{projectTabName}</span>
          </Link>
        ) : (
          <TabColumn
            href="/"
            icon={House}
            label={tBuildings('projects')}
            isActive={activeTab === 'project'}
          />
        )}

        {/* History */}
        <HistoryTabColumn
          label={t('history')}
          isActive={activeTab === 'history'}
        />

        {/* Account — the token balance replaces the person glyph on the top row
            (the standalone "Tokens" tab was reverted; the Tokens screen now
            lives under Account, mirroring Apple/Android). A deletion/alert
            account dot takes precedence over the always-on balance readout. */}
        <AccountTab
          label={t('account')}
          glyph={accountGlyph}
          balance={
            rawTokenBalance !== undefined
              ? formatTokenBalance(rawTokenBalance)
              : undefined
          }
          isActive={activeTab === 'account'}
          tokensFlag={accountIssue.attentionRows?.tokens}
          severity={accountIssue.severity}
        />

        {/* Help — reaches the Help center from anywhere, mirroring the Apple
            phone tab bar and the tablet sidebar's Help entry. */}
        <TabColumn
          href="/help"
          icon={Question}
          label={t('help')}
          isActive={activeTab === 'help'}
        />
      </div>
    </nav>
  );
}

function TabColumn({
  href,
  icon: Icon,
  label,
  isActive,
  dot,
}: {
  href: string;
  icon: React.ComponentType<{ className?: string; weight?: 'regular' | 'fill' }>;
  label: string;
  isActive: boolean;
  dot?: AccountIssueSeverity | null;
}) {
  return (
    <Link
      href={href}
      aria-label={label}
      className={cn(TAB_COLUMN_CLASS, tabTint(isActive))}
    >
      <span className={TAB_ICON_SLOT_CLASS}>
        <Icon
          className={TAB_ICON_CLASS}
          weight={isActive ? 'fill' : 'regular'}
        />
        {dot && (
          <span
            className={cn(
              'absolute -top-0.5 -end-1 w-2 h-2 rounded-full animate-attention-pulse',
              dot === 'alert' ? 'bg-red-500' : 'bg-amber-500'
            )}
          />
        )}
      </span>
      <span className={TAB_LABEL_CLASS}>{label}</span>
    </Link>
  );
}

function AccountTab({
  label,
  glyph,
  balance,
  isActive,
  tokensFlag,
  severity,
}: {
  label: string;
  glyph: AccountNavGlyph;
  balance?: string;
  isActive: boolean;
  tokensFlag?: TokenBalanceFlag;
  severity?: AccountIssueSeverity | null;
}) {
  // The glyph kind is RESOLVED BY THE SERVER (`account_chrome.kind`, precedence
  // already applied). The client never combines badge/tier fields to pick it
  // (API-driven-state-flags rule):
  //  - premium → a bare gold crown (paid Facade Kit / Pro Plan). No number,
  //    no person base; stays gold in both states for a calm premium identity;
  //  - tokens → the balance number, for everyone without a premium entitlement
  //    (tokens are universal; free users carry a daily-grant balance). Amber
  //    badge while the server flags 'warning', else a plain number; falls back
  //    to the person glyph until the balance seeds (no "0" flash);
  //  - none → the person glyph (profile not seeded yet).
  // A deletion/alert account dot takes precedence over the balance badge.
  const isPremium = glyph.kind === 'premium';
  const isTokens = glyph.kind === 'tokens';
  const isBadge = isTokens && tokensFlag === 'warning';
  const showNumber = isTokens && balance !== undefined;
  const showDot = Boolean(severity) && !isBadge;
  return (
    <Link
      href="/account"
      aria-label={showNumber ? `${label} · ${balance}` : label}
      className={cn(TAB_COLUMN_CLASS, tabTint(isActive))}
    >
      <span className={TAB_ICON_SLOT_CLASS}>
        {isPremium ? (
          // Paid entitlement: a standalone gold crown filling the glyph slot
          // (same size/placement as the sibling tab glyphs) — no person base,
          // no number. Gold in both states, but mirrors the sibling glyphs'
          // active treatment: filled when selected, outline when not.
          <Crown
            className={cn(TAB_ICON_CLASS, 'text-gold')}
            fill={isActive ? 'currentColor' : 'none'}
          />
        ) : showNumber ? (
          isBadge ? (
            <TokenBalanceBadge
              balance={balance!}
              tokensFlag={tokensFlag}
              className="text-[15px] font-semibold whitespace-nowrap"
            />
          ) : (
            <span className="text-[15px] font-semibold tabular-nums whitespace-nowrap">
              {balance}
            </span>
          )
        ) : (
          <User
            className={TAB_ICON_CLASS}
            weight={isActive ? 'fill' : 'regular'}
          />
        )}
        {showDot && (
          <span
            className={cn(
              'absolute -top-0.5 -end-1 w-2 h-2 rounded-full animate-attention-pulse',
              severity === 'alert' ? 'bg-red-500' : 'bg-amber-500'
            )}
          />
        )}
      </span>
      <span className={TAB_LABEL_CLASS}>{label}</span>
    </Link>
  );
}

function HistoryTabColumn({
  label,
  isActive,
}: {
  label: string;
  isActive: boolean;
}) {
  const pathname = usePathname();
  const activeCount = useJobQueueStore((state) =>
    state.getProcessingJobCount()
  );
  const unseenCount = useJobQueueStore((state) =>
    state.getUnseenCompletedCount()
  );
  const markJobsSeen = useJobQueueStore((state) => state.markJobsSeen);
  const hasActiveJobs = activeCount > 0;

  // Dismiss unseen badge when viewing history or concept pages
  const isHistoryPage = pathname === '/history' || pathname.startsWith('/history/');
  const conceptIdFromUrl = useMemo(() => {
    const match = pathname.match(/\/concept\/([^/]+)/);
    return match?.[1] || null;
  }, [pathname]);

  useEffect(() => {
    if (isHistoryPage) {
      markJobsSeen();
    } else if (conceptIdFromUrl) {
      markJobsSeen(conceptIdFromUrl);
    }
  }, [isHistoryPage, conceptIdFromUrl, markJobsSeen]);

  return (
    <Link
      href="/history"
      aria-label={label}
      className={cn(TAB_COLUMN_CLASS, tabTint(isActive))}
    >
      <span className={TAB_ICON_SLOT_CLASS}>
        <HistoryActivityIcon
          activeCount={activeCount}
          size="xl"
          isActive={isActive}
        />
        {!hasActiveJobs && unseenCount > 0 && (
          <span className="absolute -top-0.5 -end-1.5 bg-primary text-primary-foreground text-[9px] font-semibold min-w-3.5 h-3.5 flex items-center justify-center rounded-full px-0.5">
            {unseenCount}
          </span>
        )}
      </span>
      <span className={TAB_LABEL_CLASS}>{label}</span>
    </Link>
  );
}

function truncate(str: string, maxLength: number): string {
  return str.length > maxLength ? str.substring(0, maxLength) + '…' : str;
}
